import { getGatewayBaseUrl } from "../config";
import { PosLocalProjectionGatewayClient } from "../gateway/local-projection-client";
import { PosStockActionGatewayClient, type StockActionCommitResult } from "../gateway/stock-action-client";
import { applyStockRuntime } from "./indexed-runtime";
import { readVersionedLocalProjectionCursor } from "./local-projection-version";
import { applyStockMovementRuntime } from "./stock-movement-runtime";
import type { TrustedTerminalIdentity } from "./terminal-state";

type StockCommitScope = {
  identity: TrustedTerminalIdentity;
  deviceSecret: string;
  sessionToken: string;
};

function requireOnlineStockAction() {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    throw new Error("STOCK_ACTION_ONLINE_REQUIRED");
  }
}

async function readBackStock(scope: StockCommitScope, baseUrl: string) {
  let readbackOk = true;
  try {
    const projectionClient = new PosLocalProjectionGatewayClient({ baseUrl });
    const auth = { deviceSecret: scope.deviceSecret, sessionToken: scope.sessionToken };

    const stockCursor = await readVersionedLocalProjectionCursor(scope.identity, "STOCK");
    const stock = await projectionClient.load({ ...auth, domain: "STOCK", cursor: stockCursor, limit: 500 });
    if (!stock.ok || !stock.data) throw new Error(stock.error ?? "STOCK_READBACK_FAILED");
    await applyStockRuntime(scope.identity, stock.data);

    const historyCursor = await readVersionedLocalProjectionCursor(scope.identity, "STOCK_HISTORY");
    const history = await projectionClient.load({ ...auth, domain: "STOCK_HISTORY", cursor: historyCursor, limit: 500 });
    if (!history.ok || !history.data) throw new Error(history.error ?? "STOCK_HISTORY_READBACK_FAILED");
    await applyStockMovementRuntime(scope.identity, history.data);
  } catch {
    // Canonical mutation already succeeded. Background runtime sync is the deterministic repair
    // path, so never retry the business command only because readback transport failed.
    readbackOk = false;
  }
  return readbackOk;
}

export async function commitStockOperationalAction(input: StockCommitScope & {
  stockItemId: string;
  quantityDelta: number;
  reasonCode: string;
  note: string | null;
  requestKey: string;
}) {
  requireOnlineStockAction();

  const baseUrl = getGatewayBaseUrl();
  if (!baseUrl) throw new Error("POS_GATEWAY_NOT_CONFIGURED");

  const actionClient = new PosStockActionGatewayClient({ baseUrl });
  const committed = await actionClient.commit({
    deviceSecret: input.deviceSecret,
    sessionToken: input.sessionToken,
    stockItemId: input.stockItemId,
    quantityDelta: input.quantityDelta,
    reasonCode: input.reasonCode,
    note: input.note,
    requestKey: input.requestKey,
    clientContext: {
      runtime: "WEBPOS",
      terminal_node_id: input.identity.node_id,
      local_stock_action_authority: false,
    },
  });

  if (!committed.ok || !committed.data) {
    throw new Error(committed.error ?? "POS_STOCK_ACTION_COMMIT_FAILED");
  }

  return {
    data: committed.data as StockActionCommitResult,
    readbackOk: await readBackStock(input, baseUrl),
  };
}

export async function commitStockInternalUseBatch(input: StockCommitScope & {
  lines: Array<{ stockItemId: string; quantity: number }>;
  note: string | null;
  requestKey: string;
}) {
  requireOnlineStockAction();

  const baseUrl = getGatewayBaseUrl();
  if (!baseUrl) throw new Error("POS_GATEWAY_NOT_CONFIGURED");

  const actionClient = new PosStockActionGatewayClient({ baseUrl });
  const committed = await actionClient.commitInternalUseBatch({
    deviceSecret: input.deviceSecret,
    sessionToken: input.sessionToken,
    lines: input.lines,
    note: input.note,
    requestKey: input.requestKey,
    clientContext: {
      runtime: "WEBPOS",
      terminal_node_id: input.identity.node_id,
      local_stock_action_authority: false,
      batch_action: true,
    },
  });

  if (!committed.ok || !committed.data) {
    throw new Error(committed.error ?? "POS_STOCK_INTERNAL_USE_BATCH_COMMIT_FAILED");
  }

  return {
    data: committed.data as StockActionCommitResult,
    readbackOk: await readBackStock(input, baseUrl),
  };
}
